import { jitsiConfig } from '@/Utils/configs';
import { MEDIA_TYPE } from '@/Utils/vice-constants';

function JitsiService() {
  let connection;
  let room;

  const leaveRoom = async () => {
    try {
      await room.leave();
    } catch (error) {
      console.error(error);
    }
  };

  const closeConnection = async () => {
    await connection.disconnect();
  };

  const disconnect = async () => {
    try {
      if (room) {
        await leaveRoom();
      }
      if (connection) {
        connection.removeEventListener(
          JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED,
          onJitsiConnectionSuccess,
        );
        connection.removeEventListener(
          JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED,
          disconnect,
        );

        connection.removeEventListener(
          JitsiMeetJS.events.connection.CONNECTION_FAILED,
          onJitsiConnectionFailed,
        );
        await closeConnection();
      }
    } catch (error) {
      console.error('error disconnecting: ', error);
    }
  };

  // JitsiRoom (conference) instance
  const onJitsiConnectionSuccess = (resolve, sessionId, events, commands) => {
    room = connection.initJitsiConference(sessionId, jitsiConfig.conference);
    room.setReceiverConstraints({ defaultConstraints: { maxHeight: 1080 } });

    // Other jitsi event listeners the user wants to add
    if (!(Object.keys(events).length === 0)) {
      Object.keys(events.CONFERENCE).forEach((eventName) => {
        room.on(JitsiMeetJS.events.conference[eventName], events.CONFERENCE[eventName]);
      });

      Object.keys(events.CONN_QUALITY).forEach((eventName) => {
        room.on(JitsiMeetJS.events.connectionQuality[eventName], events.CONN_QUALITY[eventName]);
      });
    }

    Object.keys(commands).forEach((commandName) => {
      room.addCommandListener(commandName, commands[commandName]);
    });

    resolve({
      room,
      leaveRoom,
      connection,
      disconnect,
      closeConnection,
    });
  };

  const onJitsiConnectionFailed = (error, reject) => reject(error);

  /**
   * Create a jitsi connection
   * @param {string} sessionId - The session_uniqueId stripped of the (-) separator
   * @param {object} events - A key-value pair of a jitsi event name and its handler
   * @param {object} commands - A key-value pair of a custom command and its handler
   * @returns
   */
  const makeConnection = (sessionId, events = {}, commands = {}) => {
    connection = new JitsiMeetJS.JitsiConnection(null, null, jitsiConfig.options);

    return new Promise((resolve, reject) => {
      connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, () => {
        onJitsiConnectionSuccess(resolve, sessionId, events, commands);
      });
      connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, (error) => {
        console.log('connection failed', error);
        onJitsiConnectionFailed(error, reject);
      });

      connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, () => {
        disconnect();
      });

      connection.connect();
    });
  };

  /**
   * Create screenshare tracks
   * @returns
   */
  const createScreenShareTracks = async () => {
    try {
      const tracks = await JitsiMeetJS.createLocalTracks({
        devices: ['desktop'],
        firePermissionPromptIsShownEvent: true,
      });
      return {
        video: tracks.find((track) => track.type === MEDIA_TYPE.VIDEO),
        audio: tracks.find((track) => track.type === MEDIA_TYPE.AUDIO),
      };
    } catch (error) {
      throw new Error(error);
    }
  };

  const createVideoTrack = async (cameraDeviceId) => {
    try {
      const videoTrack = await JitsiMeetJS.createLocalTracks({
        devices: [MEDIA_TYPE.VIDEO],
        cameraDeviceId,
        firePermissionPromptIsShownEvent: true,
      });
      return videoTrack[0];
    } catch (error) {
      throw new Error(error);
    }
  };

  const createAudioTrack = async (micDeviceId) => {
    try {
      const audioTrack = await JitsiMeetJS.createLocalTracks({
        devices: [MEDIA_TYPE.AUDIO],
        micDeviceId,
        firePermissionPromptIsShownEvent: true,
      });
      return audioTrack[0];
    } catch (error) {
      throw new Error(error);
    }
  };

  const changeAudioOutput = async (newAudioDeviceId) => {
    try {
      await JitsiMeetJS.mediaDevices.setAudioOutputDevice(newAudioDeviceId);
    } catch (error) {
      console.error('error changing audio output/speaker');
      throw new Error(error);
    }
  };

  return {
    makeConnection,
    createAudioTrack,
    createVideoTrack,
    changeAudioOutput,
    createScreenShareTracks,
    disconnect,
  };
}

export default JitsiService;
